import { LegalPolicyFooter } from "@/components/legal/LegalPolicyLinks";
import { getPublicBottomBarPadding } from "@/components/public/PublicBottomBar";
import {
  getReturnButtonLabel,
  getSafeReturnTo,
} from "@/lib/navigation/publicReturnTo";
import { router, useLocalSearchParams } from "expo-router";
import React, { useCallback, useMemo } from "react";
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import {
  SafeAreaView,
  useSafeAreaInsets,
} from "react-native-safe-area-context";

const PUBLIC_PAGE_BOTTOM_CLEARANCE = 120;

const webScrollHostStyle =
  Platform.OS === "web"
    ? ({
        overflowX: "hidden",
        overflowY: "auto",
      } as any)
    : null;

const CORE_VALUES = [
  {
    title: "Visibility",
    description:
      "We believe every serious business deserves to be found by customers online.",
  },
  {
    title: "Trust",
    description:
      "We aim to build a platform where accurate business information, clear profiles, and responsible moderation help customers connect with confidence.",
  },
  {
    title: "Simplicity",
    description:
      "We make it easy for businesses to create and share their online presence without technical knowledge.",
  },
  {
    title: "Local Growth",
    description:
      "We support Kenyan businesses by helping them reach more customers within their towns, counties, and beyond.",
  },
  {
    title: "Connection",
    description:
      "We focus on helping customers move from search to action through calls, WhatsApp, maps, websites, and social links.",
  },
];

function goToReturnPath(returnTo: string) {
  if (returnTo) {
    router.push(returnTo as any);
    return;
  }

  router.push("/" as any);
}

export default function AboutSokoGridPage() {
  const insets = useSafeAreaInsets();
  const params = useLocalSearchParams<{ returnTo?: string | string[] }>();
  const returnTo = useMemo(
    () => getSafeReturnTo(params.returnTo),
    [params.returnTo]
  );
  const bottomPadding = Math.max(
    getPublicBottomBarPadding(insets.bottom),
    PUBLIC_PAGE_BOTTOM_CLEARANCE
  );

  const handleBack = useCallback(() => {
    goToReturnPath(returnTo);
  }, [returnTo]);

  return (
    <SafeAreaView style={styles.safeArea}>
      <ScrollView
        style={[styles.screen, webScrollHostStyle]}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: bottomPadding },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator
      >
        <View style={styles.container}>
          <Text style={styles.title}>About Us</Text>

          <View style={styles.intro}>
            <Text style={styles.bodyText}>
              Soko Grid Marketplace is a Kenyan business directory and online
              storefront platform that helps customers discover product and
              service providers across Kenya.
            </Text>
            <Text style={styles.bodyText}>
              We help businesses create searchable online profiles where
              customers can view what they offer, find their location, and
              contact them through phone, WhatsApp, website links, maps, and
              social media.
            </Text>
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>What Soko Grid Does</Text>
            <Text style={styles.sectionText}>
              Soko Grid gives businesses a professional online presence without
              needing to build a full website.
            </Text>
            <Text style={styles.sectionText}>
              Each business profile can showcase products, services, location,
              contact details, social links, WhatsApp options, and a shareable
              profile link or QR code.
            </Text>
            <Text style={styles.sectionText}>
              For customers, Soko Grid makes it easier to search, compare, and
              connect with businesses across Kenya.
            </Text>
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>Our Mission</Text>
            <Text style={styles.sectionText}>
              To help Kenyan businesses become easier to find, trust, and
              contact online by giving them simple, searchable, and
              professional digital storefronts.
            </Text>
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>Our Vision</Text>
            <Text style={styles.sectionText}>
              To become Kenya’s trusted digital marketplace where customers can
              easily discover local businesses, products, and services in every
              town and county.
            </Text>
          </View>

          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>Our Core Values</Text>
            <View style={styles.valuesList}>
              {CORE_VALUES.map((value, index) => (
                <View key={value.title}>
                  {index > 0 ? <View style={styles.divider} /> : null}
                  <View style={styles.valueItem}>
                    <Text style={styles.valueTitle}>{value.title}</Text>
                    <Text style={styles.valueText}>{value.description}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>

          <View style={styles.contactCard}>
            <Text style={styles.sectionTitle}>Need to reach us?</Text>
            <Text style={styles.sectionText}>
              For questions, support, partnerships, or business listing
              inquiries, visit our Contact page.
            </Text>
            <Pressable
              onPress={() => router.push("/contact" as any)}
              style={({ pressed }) => [
                styles.primaryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text style={styles.primaryButtonText}>Contact Us</Text>
            </Pressable>
          </View>

          <Pressable
            onPress={handleBack}
            style={({ pressed }) => [
              styles.secondaryButton,
              pressed ? styles.pressed : null,
            ]}
          >
            <Text style={styles.secondaryButtonText}>
              {getReturnButtonLabel(returnTo)}
            </Text>
          </Pressable>

          <LegalPolicyFooter returnTo="/about" style={styles.legalLinks} />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#f8fafc",
  },
  screen: {
    flex: 1,
    minHeight: 0,
    backgroundColor: "#f8fafc",
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
    paddingVertical: 22,
  },
  container: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
  },
  title: {
    fontSize: 30,
    lineHeight: 36,
    fontWeight: "900",
    color: "#111827",
  },
  intro: {
    gap: 12,
    marginTop: 12,
    maxWidth: 680,
  },
  bodyText: {
    fontSize: 16,
    lineHeight: 25,
    fontWeight: "600",
    color: "#475569",
  },
  sectionCard: {
    marginTop: 22,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    padding: 18,
    gap: 10,
  },
  sectionTitle: {
    fontSize: 21,
    lineHeight: 27,
    fontWeight: "900",
    color: "#111827",
  },
  sectionText: {
    fontSize: 15,
    lineHeight: 24,
    fontWeight: "600",
    color: "#475569",
  },
  valuesList: {
    marginTop: 2,
  },
  valueItem: {
    gap: 4,
  },
  valueTitle: {
    fontSize: 16,
    lineHeight: 22,
    fontWeight: "900",
    color: "#1f8f5a",
  },
  valueText: {
    fontSize: 15,
    lineHeight: 23,
    fontWeight: "600",
    color: "#475569",
  },
  divider: {
    height: 1,
    backgroundColor: "#edf2f7",
    marginVertical: 14,
  },
  contactCard: {
    marginTop: 22,
    borderRadius: 8,
    backgroundColor: "#ecfdf5",
    padding: 18,
    gap: 10,
  },
  primaryButton: {
    alignSelf: "flex-start",
    minHeight: 46,
    borderRadius: 8,
    backgroundColor: "#1f8f5a",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
    marginTop: 4,
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#ffffff",
  },
  secondaryButton: {
    alignSelf: "flex-start",
    minHeight: 46,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 18,
    marginTop: 20,
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: "900",
    color: "#111827",
  },
  legalLinks: {
    marginTop: 18,
  },
  pressed: {
    opacity: 0.82,
  },
});
